/**
 * Dusk (§8, §15.2 #6): the same "the candles dim…" screen on every phone, then the public
 * Bulletin and the private Dispatch (hold to view). The switch happens at the same server time
 * on every phone (`phase.startedAt` + a fixed share of the phase), so it gives nothing away.
 */
import { HoldToPeek } from "../components/HoldToPeek.tsx";
import { PlayerChip } from "../components/PlayerChip.tsx";
import { useServerNow } from "../lib/time.ts";
import { nameOf, playerOf, useView } from "../lib/view.ts";
import type { Bulletin, DispatchView, PlayerView, PublicView } from "../lib/types.ts";
import { LeakTrack } from "./RoleCard.tsx";

/** How long the candles screen lasts: a third of Dusk, at most 12 s. */
export function candlesMs(duskMs: number): number {
  return Math.min(12_000, Math.round(duskMs / 3));
}

export function Dusk() {
  const view = useView();
  const phase = view.public.phase;
  const now = useServerNow(500);
  const duration =
    phase.deadline !== null && phase.startedAt !== null ? phase.deadline - phase.startedAt : 60_000;
  const reveal = phase.startedAt === null || now >= phase.startedAt + candlesMs(duration);
  if (!reveal) {
    return (
      <div className="hero" data-testid="dusk-candles" style={{ paddingTop: "4rem" }}>
        <div className="candles">The candles dim…</div>
        <p className="muted">Every handshake of the round is being tallied.</p>
      </div>
    );
  }
  const bulletin = view.public.bulletins.find((b) => b.round === phase.round);
  const dispatch = view.dispatches.find((d) => d.round === phase.round);
  return (
    <div className="stack" data-testid="dusk">
      {bulletin ? <BulletinCard bulletin={bulletin} pub={view.public} /> : null}
      <LeakTrack pub={view.public} />
      <DispatchCard dispatch={dispatch} round={phase.round} />
    </div>
  );
}

export function BulletinCard({ bulletin, pub }: { bulletin: Bulletin; pub: PublicView }) {
  const view = pub;
  return (
    <section className="panel gold" data-testid="bulletin">
      <div className="kicker">Dusk · Round {bulletin.round}</div>
      <h2>The Bulletin</h2>
      <ul className="list">
        <li>
          <span className="grow">Leaks this round</span>
          <strong>{bulletin.leaks}</strong>
        </li>
        <li>
          <span className="grow">Leak track</span>
          <strong>
            {bulletin.leakTrack} / {bulletin.leakLimit}
          </strong>
        </li>
        <li>
          <span className="grow">Blocked attempts</span>
          <strong>{bulletin.blocked}</strong>
        </li>
        <li style={{ flexWrap: "wrap" }}>
          <span className="grow">Found collapsed</span>
          {bulletin.deaths.length === 0 ? (
            <span className="muted">No one</span>
          ) : (
            bulletin.deaths.map((id) => <PlayerChip key={id} player={playerOf(view, id)} />)
          )}
        </li>
        <li style={{ flexWrap: "wrap" }}>
          <span className="grow">Wallflowers</span>
          {bulletin.wallflowersSuppressed ? (
            <span className="muted">Not listed (Mingle ended early)</span>
          ) : bulletin.wallflowers.length === 0 ? (
            <span className="muted">None</span>
          ) : (
            bulletin.wallflowers.map((id) => <PlayerChip key={id} player={playerOf(view, id)} />)
          )}
        </li>
      </ul>
    </section>
  );
}

/** A Dispatch behind hold-to-peek. Every Dispatch has the same structure (§8.3). */
export function DispatchCard({
  dispatch,
  round,
}: {
  dispatch: DispatchView | undefined;
  round: number;
}) {
  const view = useView();
  return (
    <HoldToPeek
      label={`Hold to read your Dispatch · Round ${round}`}
      minHeight="14rem"
      testId={`dispatch-peek-${round}`}
    >
      {dispatch ? (
        <DispatchBody dispatch={dispatch} view={view} />
      ) : (
        <p className="muted">No Dispatch reached you tonight.</p>
      )}
    </HoldToPeek>
  );
}

function DispatchBody({ dispatch, view }: { dispatch: DispatchView; view: PlayerView }) {
  return (
    <div className="stack" data-testid="dispatch">
      <div>
        <div className="kicker">Your handshakes</div>
        <div>
          {dispatch.partners.length > 0
            ? dispatch.partners.map((id) => nameOf(view, id)).join(", ")
            : "None this round."}
        </div>
      </div>
      <div>
        <div className="kicker">Your report</div>
        <div style={{ fontSize: "1.15rem" }}>{dispatch.text}</div>
        {dispatch.confidedIn.map((c, i) => (
          <div key={i}>{c.text}</div>
        ))}
      </div>
      {dispatch.syndicate ? <div className="muted">{dispatch.syndicate.text}</div> : null}
      {dispatch.coachHint ? <div className="coach">{dispatch.coachHint}</div> : null}
      <div className="faint" style={{ fontStyle: "italic" }}>
        {dispatch.flavour}
      </div>
    </div>
  );
}
